import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";

// Soft UI context
import { useSoftUIController, setLayout } from "context";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";

// Toast notifications
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "layouts/pages/landing/formular-sesizare/customToast.css";

import useAPI from "utils/useApi";
import { setSession } from "utils/utils";
import { useAuthContext } from "utils/useAuthContext";

import logoStopRasismWhite from "assets/images/stop-rasism-2-white.png";
import logoStopRasismDark from "assets/images/stop-rasism-1-dark.png";
import siglaGuvernului from "assets/images/sigla-guvernului.png";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

function extractErrorMessage(error) {
  const data = error?.response?.data;
  if (!data) return error?.message || null;
  if (typeof data === "string") return data;
  if (data.detail || data.error || data.message) return data.detail || data.error || data.message;
  const firstList = Object.values(data).find((value) => Array.isArray(value) && value.length);
  return firstList ? firstList[0] : error?.message || null;
}

function formatCountdown(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function LogIn() {
  const [usernameField, setUsernameField] = useState("");
  const [passwordField, setPasswordField] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // 2FA
  const [step, setStep] = useState("credentials"); // "credentials" | "twofa"
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [twoFactor, setTwoFactor] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const otpRefs = useRef([]);
  const autoSubmittedCode = useRef("");
  // `loading` se actualizează abia la următorul render, deci nu poate opri două
  // apeluri pornite în același tick (submit + click, submit + auto-submit).
  const inFlight = useRef(false);

  const [, dispatch] = useSoftUIController();
  const client = useAPI();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthContext();

  // Dacă utilizatorul este deja autentificat, îl redirecționăm la dashboard
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard/panou-principal", { replace: true });
    }
  }, [isAuthenticated]);

  // Reset layout so the dashboard Sidenav doesn't show on the login page
  useEffect(() => {
    setLayout(dispatch, "page");
  }, []);

  // Cooldown pentru butonul de retrimitere a codului
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => setResendCooldown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Timp rămas până la expirarea codului primit pe e-mail
  useEffect(() => {
    if (!twoFactor?.expiresAt) {
      setSecondsLeft(null);
      return undefined;
    }
    const tick = () => {
      const remaining = new Date(twoFactor.expiresAt).getTime() - Date.now();
      setSecondsLeft(Math.max(0, Math.floor(remaining / 1000)));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [twoFactor?.expiresAt]);

  const completeLogin = (access, refresh) => {
    setSession(access, refresh);
    toast.success("Autentificare reușită! Redirecționare...");
    setTimeout(() => {
      window.location.href = "/dashboard/panou-principal";
    }, 1000);
  };

  const goToTwoFactorStep = (payload) => {
    setTwoFactor({
      ephemeralToken: payload.ephemeral_token,
      method: payload.method,
      email: payload.email,
      expiresAt: payload.expires_at,
    });
    setOtp(Array(OTP_LENGTH).fill(""));
    autoSubmittedCode.current = "";
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    setStep("twofa");
    setTimeout(() => otpRefs.current[0]?.focus(), 50);
  };

  // Pasul 1 — verificarea parolei. Conturile fără 2FA primesc direct token-urile.
  const handleCredentials = async (e) => {
    if (e) e.preventDefault();

    // Câmpul acceptă atât numele de utilizator, cât și adresa de e-mail.
    const identifier = usernameField.trim();

    if (!identifier || !passwordField) {
      toast.error("Vă rugăm să completați toate câmpurile");
      return;
    }

    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const response = await client.signIn(identifier, passwordField);

      if (response.status === 202 || response.two_factor_required) {
        goToTwoFactorStep(response);
        return;
      }

      if (!response.access || !response.refresh) {
        throw new Error("Răspuns neașteptat de la server. Vă rugăm să încercați din nou.");
      }
      completeLogin(response.access, response.refresh);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Nume utilizator/e-mail sau parolă incorectă");
      } else {
        toast.error(
          extractErrorMessage(error) ||
            "A apărut o eroare la autentificare. Vă rugăm să încercați din nou."
        );
      }
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    setStep("credentials");
    setOtp(Array(OTP_LENGTH).fill(""));
    setTwoFactor(null);
    setResendCooldown(0);
    autoSubmittedCode.current = "";
  };

  const handleResend = async () => {
    if (!twoFactor?.ephemeralToken || resendCooldown > 0 || resending) return;

    if (inFlight.current) return;
    inFlight.current = true;
    setResending(true);
    try {
      const data = await client.resendTwoFactorCode(twoFactor.ephemeralToken);
      setTwoFactor((prev) => ({
        ...prev,
        ephemeralToken: data?.ephemeral_token || prev.ephemeralToken,
        expiresAt: data?.expires_at || prev.expiresAt,
      }));
      setOtp(Array(OTP_LENGTH).fill(""));
      autoSubmittedCode.current = "";
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      otpRefs.current[0]?.focus();
      toast.success("Un cod nou a fost trimis pe e-mail.");
    } catch (error) {
      toast.error(extractErrorMessage(error) || "Nu am putut retrimite codul.");
    } finally {
      inFlight.current = false;
      setResending(false);
    }
  };

  const handleOtpChange = (index, rawValue) => {
    const value = rawValue.replace(/\D/g, "");
    if (!value) {
      setOtp((prev) => {
        const next = [...prev];
        next[index] = "";
        return next;
      });
      return;
    }

    setOtp((prev) => {
      const next = [...prev];
      // dacă a fost lipit mai mult de o cifră, distribuim începând cu poziția curentă
      const chars = value.split("");
      let cursor = index;
      chars.forEach((ch) => {
        if (cursor < OTP_LENGTH) {
          next[cursor] = ch;
          cursor += 1;
        }
      });
      const focusIndex = Math.min(cursor, OTP_LENGTH - 1);
      setTimeout(() => otpRefs.current[focusIndex]?.focus(), 0);
      return next;
    });
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        setOtp((prev) => {
          const next = [...prev];
          next[index] = "";
          return next;
        });
      } else if (index > 0) {
        otpRefs.current[index - 1]?.focus();
        setOtp((prev) => {
          const next = [...prev];
          next[index - 1] = "";
          return next;
        });
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      otpRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((ch, i) => {
      next[i] = ch;
    });
    setOtp(next);
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    setTimeout(() => otpRefs.current[focusIndex]?.focus(), 0);
  };

  // Pasul 2 — verificarea codului 2FA + autentificare efectivă.
  const handleVerify = async (e) => {
    if (e) e.preventDefault();

    const code = otp.join("");
    if (code.length < OTP_LENGTH) {
      toast.error("Vă rugăm să introduceți codul complet");
      return;
    }

    if (!twoFactor?.ephemeralToken) {
      toast.error("Sesiunea de autentificare a expirat. Reintroduceți datele de acces.");
      handleBackToCredentials();
      return;
    }

    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const { access, refresh } = await client.verifyTwoFactor(twoFactor.ephemeralToken, code);
      completeLogin(access, refresh);
    } catch (error) {
      if (error.response?.status === 429) {
        // Codul poate fi corect — îl păstrăm ca să poată reîncerca după așteptare.
        toast.error(
          extractErrorMessage(error) ||
            "Prea multe încercări. Vă rugăm să așteptați puțin înainte de a reîncerca."
        );
      } else {
        setOtp(Array(OTP_LENGTH).fill(""));
        autoSubmittedCode.current = "";
        setTimeout(() => otpRefs.current[0]?.focus(), 0);
        toast.error(extractErrorMessage(error) || "Codul introdus este invalid sau a expirat.");
      }
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  };

  // Trimitere automată imediat ce cele 6 cifre au fost completate.
  useEffect(() => {
    if (step !== "twofa" || loading) return;
    const code = otp.join("");
    if (code.length !== OTP_LENGTH || autoSubmittedCode.current === code) return;
    autoSubmittedCode.current = code;
    handleVerify();
  }, [otp, step, loading]);

  return (
    <SoftBox
      display="flex"
      minHeight="100vh"
      sx={{ background: "#f0f4f8" }}
    >
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* ── LEFT PANEL ── */}
      <SoftBox
        display={{ xs: "none", md: "flex" }}
        flexDirection="column"
        justifyContent="space-between"
        width="45%"
        minHeight="100vh"
        sx={{
          background: "linear-gradient(160deg, #0c1f3f 0%, #1e3a5f 55%, #1a4a7a 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* decorative circles */}
        <SoftBox sx={{
          position: "absolute", width: 340, height: 340, borderRadius: "50%",
          border: "1px solid rgba(255,255,255,0.06)", top: -80, right: -80,
        }} />
        <SoftBox sx={{
          position: "absolute", width: 220, height: 220, borderRadius: "50%",
          border: "1px solid rgba(255,255,255,0.05)", top: 60, right: 30,
        }} />
        <SoftBox sx={{
          position: "absolute", width: 400, height: 400, borderRadius: "50%",
          border: "1px solid rgba(255,255,255,0.04)", bottom: -120, left: -100,
        }} />

        {/* Top: logo + platform name */}
        <SoftBox px={5} pt={5} position="relative" zIndex={1}>
          <SoftBox display="flex" alignItems="center" gap={2} mb={5}>
            <SoftBox
              component="img"
              src={siglaGuvernului}
              alt="Guvernul României"
              sx={{ width: 56, height: 56, objectFit: "contain", display: "block", flexShrink: 0 }}
            />
            <SoftBox aria-hidden="true" sx={{ width: "1.5px", height: "42px", bgcolor: "rgba(255,255,255,0.35)", flexShrink: 0 }} />
            <SoftBox
              component="img"
              src={logoStopRasismWhite}
              alt="Stop Rasism"
              sx={{ height: 48 }}
            />
          </SoftBox>

          <SoftTypography variant="h3" sx={{ color: "#ffffff", fontWeight: 800, lineHeight: 1.25, mb: 2 }}>
            Panou de<br />administrare
          </SoftTypography>
          <SoftTypography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.88rem", lineHeight: 1.8, maxWidth: 340 }}>
            Sistem integrat pentru raportarea incidentelor antisemite, rasiste și xenofobe.
          </SoftTypography>
        </SoftBox>

        {/* Feature pills */}
        <SoftBox px={5} position="relative" zIndex={1}>
          {[
            { icon: "flag", text: "Sesizări privind incidente rasiste, antisemite și xenofobe" },
            { icon: "manage_search", text: "Monitorizare și procesare în timp real de către instituții" },
          ].map((item, i) => (
            <SoftBox key={i} display="flex" alignItems="center" gap={1.5} mb={2.5}>
              <SoftBox sx={{
                width: 36, height: 36, borderRadius: "10px",
                bgcolor: "rgba(255,255,255,0.1)",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <Icon sx={{ color: "rgba(255,255,255,0.75)", fontSize: "1rem !important" }}>{item.icon}</Icon>
              </SoftBox>
              <SoftTypography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.8rem", lineHeight: 1.5 }}>
                {item.text}
              </SoftTypography>
            </SoftBox>
          ))}
        </SoftBox>

        {/* Bottom footer */}
        <SoftBox px={5} pb={4} position="relative" zIndex={1}>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 2 }} />
          <SoftTypography sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.7rem" }}>
            © {new Date().getFullYear()} Platforma a fost dezvoltată și este întreținută de ICI București.
          </SoftTypography>
        </SoftBox>
      </SoftBox>

      {/* ── RIGHT PANEL ── */}
      <SoftBox
        flex={1}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        px={{ xs: 3, sm: 6, md: 8 }}
        py={5}
        sx={{ background: "#f0f4f8" }}
      >
        {/* Mobile-only logo */}
        <SoftBox display={{ xs: "flex", md: "none" }} alignItems="center" gap={1.5} mb={5}>
          <SoftBox
            component="img"
              src={logoStopRasismDark}
              alt="Stop Rasism"
              sx={{ height: 40 }}
          />
          <SoftTypography sx={{ fontWeight: 700, fontSize: "0.75rem", color: "#1e3a5f",
            textTransform: "uppercase", letterSpacing: "1.2px", lineHeight: 1.4 }}>
            Platforma<br />de Sesizări
          </SoftTypography>
        </SoftBox>

        <SoftBox width="100%" maxWidth={420}>
          {/* Header */}
          <SoftBox mb={4}>
            <SoftTypography variant="h4" sx={{ color: "#1e3a5f", fontWeight: 800, lineHeight: 1.2 }}>
              {step === "credentials" ? "Autentificare" : "Verificare în doi pași"}
            </SoftTypography>
            <SoftTypography sx={{ color: "#8392ab", fontSize: "0.85rem", mt: 0.5 }}>
              {step === "credentials"
                ? "Introduceți datele de acces ale contului instituțional"
                : `Am trimis un cod de 6 cifre la adresa ${twoFactor?.email || "de e-mail asociată contului"}`}
            </SoftTypography>
          </SoftBox>

          {/* Form card */}
          <SoftBox
            sx={{
              background: "#ffffff",
              borderRadius: "16px",
              p: { xs: 3, sm: 4 },
              boxShadow: "0 4px 30px rgba(0,0,0,0.08)",
              border: "1px solid rgba(0,0,0,0.05)",
            }}
          >
            {step === "credentials" ? (
              <SoftBox component="form" role="form" onSubmit={handleCredentials}>
                {/* Nume utilizator sau e-mail */}
                <SoftBox mb={3}>
                  <SoftTypography variant="caption" fontWeight="bold"
                    sx={{ color: "#344767", textTransform: "uppercase", letterSpacing: "0.5px",
                      fontSize: "0.65rem", display: "block", mb: 0.75 }}>
                    Nume utilizator sau e-mail
                  </SoftTypography>
                  <SoftInput
                    type="text"
                    placeholder="utilizator sau utilizator@institutie.gov.ro"
                    value={usernameField}
                    onChange={(e) => setUsernameField(e.target.value)}
                    icon={{ component: "person", direction: "left" }}
                    autoComplete="username"
                    inputProps={{ autoCapitalize: "none", autoCorrect: "off", spellCheck: false }}
                    required
                  />
                </SoftBox>

                {/* Password */}
                <SoftBox mb={4}>
                  <SoftTypography variant="caption" fontWeight="bold"
                    sx={{ color: "#344767", textTransform: "uppercase", letterSpacing: "0.5px",
                      fontSize: "0.65rem", display: "block", mb: 0.75 }}>
                    Parolă
                  </SoftTypography>
                  <SoftBox position="relative">
                    <SoftInput
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••••"
                      value={passwordField}
                      onChange={(e) => setPasswordField(e.target.value)}
                      icon={{ component: "lock", direction: "left" }}
                      autoComplete="current-password"
                      required
                    />
                    <SoftBox
                      onClick={() => setShowPassword((v) => !v)}
                      sx={{
                        position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                        cursor: "pointer", color: "#8392ab", display: "flex", alignItems: "center",
                        zIndex: 2,
                      }}
                    >
                      <Icon sx={{ fontSize: "1.1rem !important" }}>
                        {showPassword ? "visibility_off" : "visibility"}
                      </Icon>
                    </SoftBox>
                  </SoftBox>
                </SoftBox>

                <SoftButton
                  type="submit"
                  fullWidth
                  disabled={loading}
                  sx={{
                    background: "linear-gradient(135deg, #1e3a5f, #2563a0)",
                    color: "white",
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    letterSpacing: "0.5px",
                    py: 1.5,
                    borderRadius: "10px",
                    boxShadow: "0 4px 15px rgba(30,58,95,0.35)",
                    "&:hover": { background: "linear-gradient(135deg, #162d4a, #1e4f85)", boxShadow: "0 6px 20px rgba(30,58,95,0.4)" },
                    "&:disabled": { opacity: 0.7 },
                    textTransform: "uppercase",
                  }}
                >
                  {loading ? (
                    <SoftBox display="flex" alignItems="center" justifyContent="center" gap={1}>
                      <CircularProgress size={16} sx={{ color: "white" }} />
                      Se verifică...
                    </SoftBox>
                  ) : (
                    <SoftBox display="flex" alignItems="center" justifyContent="center" gap={1}
                      sx={{ color: "#ffffff !important" }}>
                      <Icon sx={{ fontSize: "1rem !important", color: "#ffffff !important" }}>login</Icon>
                      Continuă
                    </SoftBox>
                  )}
                </SoftButton>
              </SoftBox>
            ) : (
              <SoftBox component="form" role="form" onSubmit={handleVerify}>
                <SoftTypography variant="caption" fontWeight="bold"
                  sx={{ color: "#344767", textTransform: "uppercase", letterSpacing: "0.5px",
                    fontSize: "0.65rem", display: "block", mb: 1.5, textAlign: "center" }}>
                  Cod de autentificare
                </SoftTypography>

                {/* 6-digit OTP */}
                <SoftBox
                  display="flex"
                  justifyContent="center"
                  gap={{ xs: 1, sm: 1.5 }}
                  mb={2}
                  onPaste={handleOtpPaste}
                >
                  {otp.map((digit, index) => (
                    <SoftBox
                      key={index}
                      component="input"
                      ref={(el) => { otpRefs.current[index] = el; }}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onFocus={(e) => e.target.select()}
                      sx={{
                        width: { xs: 44, sm: 52 },
                        height: { xs: 52, sm: 60 },
                        textAlign: "center",
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        color: "#1e3a5f",
                        border: "1px solid #d2dbe6",
                        borderRadius: "12px",
                        outline: "none",
                        background: "#f8fafc",
                        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                        "&:focus": {
                          borderColor: "#2563a0",
                          boxShadow: "0 0 0 3px rgba(37,99,160,0.15)",
                          background: "#ffffff",
                        },
                      }}
                    />
                  ))}
                </SoftBox>

                <SoftTypography
                  sx={{ color: secondsLeft === 0 ? "#d9534f" : "#8392ab", fontSize: "0.75rem",
                    textAlign: "center", display: "block", mb: 3, minHeight: "1.2em" }}
                >
                  {secondsLeft === null
                    ? ""
                    : secondsLeft > 0
                      ? `Codul expiră în ${formatCountdown(secondsLeft)}`
                      : "Codul a expirat. Solicitați unul nou."}
                </SoftTypography>

                <SoftButton
                  type="submit"
                  fullWidth
                  disabled={loading}
                  sx={{
                    background: "linear-gradient(135deg, #1e3a5f, #2563a0)",
                    color: "white",
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    letterSpacing: "0.5px",
                    py: 1.5,
                    borderRadius: "10px",
                    boxShadow: "0 4px 15px rgba(30,58,95,0.35)",
                    "&:hover": { background: "linear-gradient(135deg, #162d4a, #1e4f85)", boxShadow: "0 6px 20px rgba(30,58,95,0.4)" },
                    "&:disabled": { opacity: 0.7 },
                    textTransform: "uppercase",
                  }}
                >
                  {loading ? (
                    <SoftBox display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={16} sx={{ color: "white" }} />
                      Se autentifică...
                    </SoftBox>
                  ) : (
                    <SoftBox display="flex" alignItems="center" justifyContent="center" gap={1}
                      sx={{ color: "#ffffff !important" }}>
                      <Icon sx={{ fontSize: "1rem !important", color: "#ffffff !important" }}>verified_user</Icon>
                      Verifică și autentifică
                    </SoftBox>
                  )}
                </SoftButton>

                <SoftBox mt={2.5} display="flex" alignItems="center" justifyContent="space-between">
                  <SoftBox
                    onClick={handleBackToCredentials}
                    sx={{
                      display: "flex", alignItems: "center", gap: 0.5,
                      cursor: "pointer", color: "#8392ab",
                      "&:hover": { color: "#1e3a5f" },
                    }}
                  >
                    <Icon sx={{ fontSize: "1rem !important" }}>arrow_back</Icon>
                    <SoftTypography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "inherit" }}>
                      Înapoi
                    </SoftTypography>
                  </SoftBox>

                  <SoftBox
                    onClick={handleResend}
                    sx={{
                      display: "flex", alignItems: "center", gap: 0.5,
                      cursor: resendCooldown > 0 || resending ? "default" : "pointer",
                      color: resendCooldown > 0 || resending ? "#c3ccd8" : "#2563a0",
                      "&:hover": { color: resendCooldown > 0 || resending ? "#c3ccd8" : "#1e3a5f" },
                    }}
                  >
                    <Icon sx={{ fontSize: "1rem !important" }}>refresh</Icon>
                    <SoftTypography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "inherit" }}>
                      {resending
                        ? "Se trimite..."
                        : resendCooldown > 0
                          ? `Retrimite (${resendCooldown}s)`
                          : "Retrimite codul"}
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            )}
          </SoftBox>

          {/* Security notice */}
          <SoftBox mt={3} display="flex" alignItems="flex-start" gap={1}>
            <Icon sx={{ color: "#adb5bd", fontSize: "0.9rem !important", mt: "2px", flexShrink: 0 }}>
              info
            </Icon>
            <SoftTypography sx={{ color: "#adb5bd", fontSize: "0.72rem", lineHeight: 1.6 }}>
              Această platformă este destinată exclusiv personalului autorizat. 
              Orice acces neautorizat este monitorizat și poate fi sancționat conform legii.
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

export default LogIn;
